import { uid } from 'quasar';
import { defineStore } from 'pinia';
import { i18n } from 'src/boot/i18n';
import { SessionStorage } from 'quasar';
import type { dynamicObj } from 'src/pages/assets';
import poActionsUtil from 'src/utils/poActionsUtil';
import { ApiExceptions } from 'src/exceptions/index';
import { getUserDetails } from 'src/utils/UserDetails';
import { formBuilderInputTypes } from 'src/formBuilder/formBuilderConstants';
import {  isEmptyString, isUndefinedOrNull, objLen } from 'src/utils/general';
import { toBoolPreference } from 'src/utils/preferenceBool';
import { useDocLockStore, useSettingStore, useDocAttributesStore, useFormBuilderStore, useAuthTokenStore, useDocTypeFieldsStore } from 'src/store/main';
import { allowedDocTypes, readOnlyDocStatus, po_table_in_layout_builder, accountingEffectedByFields, AI_table_extraction_setting, doc_lock_key, CONSTANTS, QUOTE, restAccountingByFields, ACCOUNTING_ELEMENT_FIELD_PREFIXES, auto_accounting_reset_data_on_change_setting } from 'src/constants';
import { documentDetailApiService, documentPOApiService, docScriptApiService, workflowApiService, documentTypeApiService } from 'src/services';

export const useDocumentStore = defineStore('document_store', {
  state: () => ({
    show_all_menu_btns: false,
    fieldErrors: {} as dynamicObj,
    tb_version: 'v3',
    has_ai_table: false,
    extractable: true,
    sub_doc_type: '',
    doctype: '',
    regex: new Map(),
    language_origin: '',
    processing: false,
    auto_saving: false,
    ext_table_train: true,
    auto_saved_done: false,
    notifications: [] as any,
    record: {} as dynamicObj,
    script_response: {} as dynamicObj,
    amount_format_locale: 'en_US',
    apiData: [] as dynamicObj,
    confirmationForPoConnectionReset: false,
    error: false,
    connectionId: '',
    error_message: '',
    doc_type_sub_type: '',
    doc_status: '',
    doc_loaded: false,
    saving: false,
    pages_list: [] as dynamicObj,
    current_page: 1,
    document_version: 0,
    selected_doc_type: '',
    tablePermissions: {} as dynamicObj,
    fieldPermissions: {} as dynamicObj,
    select_table_action: 'apply_to_current_page',
    select_table_action_list: [
      {
        value: 'apply_to_current_page',
        label: 'Apply to current page',
      },
      {
        value: 'apply_to_all',
        label: 'Apply to all page',
      },
    ],
    is_extracted_table_update: false,

    visibleImages: [] as any,
    image: '',
    page: 1,
    images: [] as dynamicObj,
    routesList: ['/tbl_ext_v3', '/2a', '/field_validation_v1', '/rejected'],

    doc_annotations: [] as dynamicObj,

    po_enable: false,
    enableExportButton: false,
    is_manual_training_allowed: false,
    api_request_id: '',
    supplier: '',
    script_api_timestamp: 0,

    remove_unmapped_columns: false,
    fieldsNotDisplayed: {} as dynamicObj,
    script_error_count: 0,
    lookup_configurations: new Map(),
    key_old_fields_state: 'doc_old_fields_state',
    old_fields_state: [] as dynamicObj[],
    saved_fields_for_autotrigger: 'saved_fields_for_autotrigger',
    old_auto_trigger_state: [] as dynamicObj[],
    po_items: {} as dynamicObj,
    po_items_loading: false,
    po_items_loaded: false,
    purchase_order_columns: ['purchase_order_number', 'purchase_order'],
    header_po_number: '',
    is_view_readonly: false,
    delete_tab_po_number: '',
    delete_tab_confirmation: false,
    confirm_de_active_dialog: false,
    fetch_po_items: true,
    tables_list: [] as any,
    chargesFields: {} as any,
    authTokenStore: useAuthTokenStore(),
    docTypeFieldsStore: useDocTypeFieldsStore(),
    bannerBg: 'error-banner',
    ignore_doc_type_specific_validation: false,
    quotesTableIdsNeedUserAction: [] as string[],
  }),
  getters: {
    get_cached_data_id: () => {
      const doc_info = SessionStorage.getItem('doc_info') as dynamicObj;
      if (doc_info && doc_info.hasOwnProperty('doc_id')) {
        return String(doc_info.doc_id);
      }
      return '';
    },
    hasApiData: (state) => {
      return (objLen(state.record) !== 0 && !state.error) as boolean;
    },
  },
  actions: {
    async saveMappingData(data: dynamicObj) {
      const response: dynamicObj =
        await documentDetailApiService.save_mapping_data(data);
      return response;
    },
    async getCoaMappingApi(customer_product_code: string) {
      if (!customer_product_code) {
        return { success: false, message: 'Supplier ID is required' };
      }
      const response: dynamicObj =
        await documentDetailApiService.get_coa_mapping(customer_product_code);
      return response;
    },
    async saveDocumentWithCoaFields(doc_id: string, coa_fields: dynamicObj) {
      if (!coa_fields) {
        return { success: false, message: 'No Coa Fields found' };
      }
      this.record.coa_item = coa_fields;
      const response = await this.saveDocument(doc_id);
      return response;
    },
    async rejectCoa(reject_data: dynamicObj): Promise<dynamicObj> {
      const response: dynamicObj =
        await documentDetailApiService.rejectCoa(reject_data);
      return response;
    },
    async getResponseCoa(product_code: any): Promise<dynamicObj> {
      if (!product_code) {
        return { success: false, message: 'Product code is required' };
      }
      const response: dynamicObj =
        await documentDetailApiService.get_response_coa(product_code);
      return response;
    },
    getDocStatusById(document_id: string) {
      return documentDetailApiService.get_document_status(document_id);
    },
    resetAllStoreData() {
      this.removeCache();
      this.$reset();
      this.resetStoreVars();
    },
    resetDocStore(to: string) {
      let resetStore = true;
      for (const urlPreFix of this.routesList) {
        if (to.startsWith(urlPreFix)) {
          resetStore = false;
          break;
        }
      }

      if (resetStore) {
        this.resetAllStoreData();
      }
    },

    resetStoreVars() {
      this.pages_list = [];
      this.api_request_id = '';
      this.fieldsNotDisplayed = {} as dynamicObj;
      this.po_items = {} as dynamicObj;
      const doc_attribute_store = useDocAttributesStore();
      doc_attribute_store.resetDocAttributes();
    },

    setApiData(data: any) {
      this.apiData = data;
    },

    async setDataToStoreAndStorage(data: any) {
      if (!data.layout) {
        const doc_info = SessionStorage.getItem('doc_info') as dynamicObj;
        if (doc_info) {
          data.layout = doc_info.layout;
        }
      }
      this.record = {};
      this.apiData = {};
      this.record = data;
      this.apiData = data;
      this.setDataToCache();
      await this.setDocDataVariables();
    },

    async setRecordFromCache() {
      if (this.doc_loaded) {
        return true;
      }
      this.record = {} as dynamicObj;

      const doc_info = SessionStorage.getItem('doc_info');
      if (doc_info !== null) {
        this.record = doc_info as dynamicObj;
        this.old_fields_state = JSON.parse(
          JSON.stringify(this.getFieldsOldState()),
        );
        this.saveFieldsOldState(this.record);
      } else {
        return false;
      }

      this.setExtractionTableDataFromCache();

      if (SessionStorage.has('doc_qualitycheck')) {
        this.record.qualitycheck = SessionStorage.getItem('doc_qualitycheck');
      }

      if (SessionStorage.has('doc_time_logs')) {
        this.record.time_logs = SessionStorage.getItem('doc_time_logs');
      }

      this.resetStoreVars();
      await this.setDocDataVariables();
      this.setMsgBannerColor();
      this.doc_loaded = true;
      return true;
    },

    setExtractionTableDataFromCache() {
      if (SessionStorage.has('doc_tables')) {
        this.record.tables = SessionStorage.getItem('doc_tables');
      }
    },

    setDataToCache() {
      try {
        const data = JSON.parse(JSON.stringify(this.record));

        if (data.tables && data.hasOwnProperty('tables')) {
          SessionStorage.set('doc_tables', data.tables);
          delete data.tables;
        }

        if (data.qualitycheck && data.hasOwnProperty('qualitycheck')) {
          SessionStorage.set('doc_qualitycheck', data.qualitycheck);
          delete data.qualitycheck;
        }

        if (data.time_logs && data.hasOwnProperty('time_logs')) {
          SessionStorage.set('doc_time_logs', data.time_logs);
          delete data.time_logs;
        }

        SessionStorage.set('doc_info', data);
      } catch (err) {
        logError(err);
      }
    },

    removeCache() {
      SessionStorage.remove('doc_info');
      SessionStorage.remove('doc_time_logs');
      SessionStorage.remove('doc_qualitycheck');
      SessionStorage.remove('doc_tables');
      SessionStorage.remove(doc_lock_key);
      SessionStorage.remove(this.key_old_fields_state);
      SessionStorage.remove(this.saved_fields_for_autotrigger);
    },

    async getDocumentById(id: string) {
      try {
        this.resetStoreVars();

        this.doc_loaded = false;
        const data = { doc_id: id };
        const response = await documentDetailApiService.getById(data);

        if (
          response.success == false ||
          response.hasOwnProperty('error_code')
        ) {
          throw response;
        }

        this.record = {};
        // Unwrap data envelope if API returns {data: {...}, success: true}
        // Other methods (getDocRecord, reloadDocument) already do this correctly
        this.record = response.data ?? response;
        this.apiData = response.data ?? response;

        if (this.record.hasOwnProperty('status') && this.record.status === 'error') {
          this.doc_loaded = true;
          this.handleDocError('error_status');
          return;
        }

        if (!this.record.doc_id) {
          this.doc_loaded = true;
          this.handleDocError('invalid');
          return;
        }
        this.saveFieldsOldState(this.record);
        this.saveFieldsStateForAutotrigger(this.record.fields);
        this.old_fields_state = JSON.parse(
          JSON.stringify(this.getFieldsOldState()),
        );
        this.setDataToCache();
        this.setMsgBannerColor();
        await this.setDocDataVariables();
        this.doc_loaded = true;
      } catch (error: any) {
        logError(error);
        this.handleServerError(error);
      } finally {
        this.processing = false;
      }
    },
    setMsgBannerColor() {
      if (this.record.status != 'ready_for_validation') {
        this.bannerBg = 'bg-grey-7';
      }
    },

    handleServerError(response: dynamicObj): boolean {
      if (!response) {
        console.error('handleServerError: Empty or null response received.');
        return false;
      }

      const { error_code, message, response: nestedResponse } = response;

      if (error_code) {
        switch (error_code) {
          case 'NOT_ENOUGH_PERMISSIONS':
          case 'NOT_ENOUGH_PERMISSIONS_TO_VIEW_DOCUMENT':
            this.handleDocError('permission');
            return true;
          case 'NOT_FOUND':
            this.handleDocError('record_not_found');
            return true;
          default:
            this.handleDocError('general');
            console.warn('Unhandled error_code:', error_code);
            return true;
        }
      }

      if (nestedResponse?.data?.error_code === 'NOT_ENOUGH_PERMISSIONS') {
        this.handleDocError('permission');
        return true;
      }

      if (message === 'No record found') {
        this.handleDocError('record_not_found');
        return true;
      }

      // Fallback for unrecognized errors
      console.warn(
        'handleServerError: Response does not contain recognized error_code or message.',
        response,
      );
      this.handleDocError('general'); // Handle as a general error
      return true;
    },

    handleDocError(message: string) {
      this.error = true;
      this.error_message = message;
    },

    // electronic documents which cannot be extracted
    // make field not extractable
    // should not be able to see extraction related functionality
    // user can update field manually
    isDocExtractable() {
      if (this.record.extraction_type === 'XML_DOCUMENT') {
        return false;
      }
      return true;
    },

    async getExtAppPODocumentByID(id: string) {
      const response = await documentPOApiService.getExtAppPODocumentByID(id);
      if (response.success !== true) {
        throw { message: i18n.t('INVALID_DOCUMENT') };
      }

      this.record = response.data;
      this.apiData = response.data;
      this.apiData.ext_table_train = true;
      await this.setDocDataVariables();
    },

    cleanupDataforApi(data: dynamicObj) {
      try {
        // iterate and remove last empty subgroup
        const form_builder_store = useFormBuilderStore();
        const { formLayout, formBuilderModel } = form_builder_store;

        let selectedGroup = 0;
        const subGroupsToRemove = [];

        if (Array.isArray(formLayout)) {
          for (const [groupIndex, group] of formLayout.entries()) {
            if (!group || Array.isArray(group.rows)) {
              continue;
            }
            for (const [subGroupIndex, subGroup] of group.rows.entries()) {
              if (
                subGroup.fieldType === formBuilderInputTypes.SUB_GROUP &&
                subGroup.isParent
              ) {
                const { nextIndex, lastVisitedField } =
                  form_builder_store.findIndexToAddNewItem(
                    subGroupIndex,
                    subGroup.rows[0].name,
                    groupIndex,
                    subGroup.rows[0].name,
                  );

                const lastChildIndex = nextIndex - 1;

                // means no child item
                if (lastChildIndex == subGroupIndex) {
                  continue;
                }

                const isValidationFailed = form_builder_store.validateLastItem(
                  groupIndex,
                  lastChildIndex,
                );
                if (isValidationFailed) {
                  selectedGroup = groupIndex;
                  subGroupsToRemove.push(lastChildIndex);

                  // remove the group fields
                  const lastSubGroup = JSON.parse(
                    JSON.stringify(formLayout[groupIndex].rows[lastChildIndex]),
                  );
                  data.fields = data.fields.filter(
                    (field: any) =>
                      !lastSubGroup.rows.some(
                        (item: any) => field.name === item.field_name,
                      ),
                  );
                }
              }
            }
          }
        }

        if (data.hasOwnProperty('table_data_copy')) {
          delete data['table_data_copy'];
        }

        if (subGroupsToRemove.length == 0) {
          return;
        }

        for (let i = subGroupsToRemove.length - 1; i >= 0; i--) {
          formLayout[selectedGroup].rows.splice(subGroupsToRemove[i], 1);
        }
      } catch (error) {
        logError(error);
      }
    },

    async saveExtAppDocument(id: string): Promise<dynamicObj> {
      const data: any = this.record;
      this.cleanupDataforApi(data);
      const response: dynamicObj =
        await documentPOApiService.saveExtAppDocument(data, id);
      return response;
    },

    async saveDataWithDocLock(id: string): Promise<dynamicObj> {
      const doc_lock_store = useDocLockStore();
      const actionLockAcquired = await doc_lock_store.hasActionAcquiredLock();
      if (!actionLockAcquired) {
        return {};
      }

      const response = await this.saveDocument(id);
      if (response.success === false) {
        if (response.message) throw { message: response.message };
        throw {};
      }
      return response;
    },

    async saveDocument(
      id: string,
      approve_or_reject = false,
    ): Promise<dynamicObj> {
      this.saving = true;
      const data: any = JSON.parse(JSON.stringify(this.record));
      this.cleanupDataforApi(data);

      if (
        data.status == 'validated_pending_approval' ||
        data.status == 'approved_pending_second_approval'
      ) {
        if (approve_or_reject) {
          data.first_approver = getUserDetails().id;
        } else {
          const form_builder_store = useFormBuilderStore();
          const firstApproverField =
            form_builder_store.formBuilderModel['first_approver'];

          if (firstApproverField && firstApproverField.formatted_value) {
            data.first_approver = firstApproverField.formatted_value;
          }
        }
      }

      this.old_fields_state = JSON.parse(
        JSON.stringify(this.getFieldsOldState()),
      );
      const response: dynamicObj = await documentDetailApiService.save(
        data,
        id,
      );
      if (response.success !== false) {
        this.record = response.data;
        this.apiData = response.data;
        await this.setDocDataVariables();
        this.saveFieldsOldState(this.record);
        this.triggerWorkflowIfRequired(this.old_fields_state);
        // Will be removed once api response is fixed
        if (!this.record.hasOwnProperty('layout')) {
          this.record.layout = data.layout;
        }
        this.saving = false;
      }

      return response;
    },

    checkifFieldChanged(field_name: string) {
      const flattenFields = this.getFlattenFields(this.old_fields_state);
      const field = this.record.fields.find(
        (item: dynamicObj) => item.name === field_name,
      );
      if (
        flattenFields.hasOwnProperty(field.name) === true &&
        flattenFields[field.name].formatted_value === field.formatted_value
      ) {
        return false;
      }
      return true;
    },

    checkifFieldChangedForTrigger(field_name: string) {
      try {
        this.old_auto_trigger_state = JSON.parse(
          JSON.stringify(this.getFieldsStateForAutotrigger()),
        );
        const flattenFields = this.getFlattenFields(
          this.old_auto_trigger_state,
        );
        const field = this.record.fields.find(
          (item: dynamicObj) => item.name === field_name,
        );
        if (!field) {
          return false;
        }
        if (
          flattenFields.hasOwnProperty(field.name) === true &&
          flattenFields[field.name].formatted_value === field.formatted_value
        ) {
          return false;
        }
        return true;
      } catch (err) {
        logError(err);
        return true;
      }
    },

    triggerWorkflowIfRequired(fields: dynamicObj[]) {
      if (fields.length === 0) {
        return;
      }
      const form_builder_store = useFormBuilderStore();
      const { formBuilderConfigModel } = form_builder_store;

      const value_chagne_fields = {} as dynamicObj[];
      const flattenFields = this.getFlattenFields(fields);

      for (const field of this.record.fields) {
        // if field is not changed
        if (
          flattenFields.hasOwnProperty(field.name) === true &&
          flattenFields[field.name].formatted_value === field.formatted_value
        ) {
          continue;
        }

        // if field is changed and has workflow
        if (
          formBuilderConfigModel.hasOwnProperty(field.name) &&
          formBuilderConfigModel[field.name].run_workflow_on_change === true &&
          formBuilderConfigModel[field.name].workflow_id_on_change !== ''
        ) {
          value_chagne_fields[field.name] = {
            workflow_id:
              formBuilderConfigModel[field.name].workflow_id_on_change,
          };
        }
      }

      if (objLen(value_chagne_fields) === 0) {
        return;
      }

      for (const [field_name, field] of Object.entries(value_chagne_fields)) {
        this.triggerWorkflow(field_name, field);
      }
    },

    async triggerWorkflow(field_name: string, field: dynamicObj) {
      try {
        await workflowApiService.triggerWorkflowOnFieldChange({
          workflow_id: field.workflow_id,
          doc_id: this.record.doc_id,
        });
      } catch (err) {
        const msg = ApiExceptions.getErrorMessage(err as dynamicObj);
        logError(err);
        return {
          success: false,
          message: msg,
        };
      }
    },

    getFlattenFields(fields: dynamicObj[]) {
      const flattenFields = {} as dynamicObj;
      for (const field of fields) {
        flattenFields[field.name] = field;
      }
      return flattenFields;
    },

    updateRequestId() {
      this.api_request_id = uid();
      logError('Updating Script API Request ID', this.api_request_id);
    },
    resetRequestId() {
      this.api_request_id = '';
    },

    getScriptChangedFields() {
      // Compare response.fields with record.fields and if the formatted_value is different in each field then add it to the list
      const changed_fields: dynamicObj[] = [];
      for (const responseField of this.script_response.fields) {
        const recordField = this.record.fields.find(
          (recordField: dynamicObj) => recordField.name === responseField.name,
        );
        if (!recordField) {
          changed_fields.push(responseField);
          continue;
        }
        if (
          recordField &&
          recordField.formatted_value !== responseField.formatted_value
        ) {
          changed_fields.push(responseField);
        }
      }
      return changed_fields;
    },

    async applyScriptOnDocument() {
      try {
        if (!this.api_request_id) {
          return false;
        }
        logError('applyScriptOnDocument request_id', this.api_request_id);
        this.record.uid = this.api_request_id;
        const data = {
          doc_json: JSON.stringify(this.record),
        };

        const response: dynamicObj =
          await docScriptApiService.applyScript(data);
        if (!response) {
          return false;
        }

        if (response.data.message) throw { message: response.data.message };
        if (this.api_request_id !== response.data.uid) {
          return false;
        }
        logError(
          'applyScriptOnDocument response',
          this.api_request_id,
          response.data.uid,
        );
        this.resetRequestId();
        this.script_response = response.data;
        this.saving = false;
        return true;
      } catch (err: any) {
        logError(err);
        return false;
      }
    },

    async updateRecordAfterScript() {
      this.old_fields_state = JSON.parse(
        JSON.stringify(this.getFieldsOldState()),
      );
      this.record = this.script_response;
      this.apiData = this.script_response;
      await this.setDocDataVariables();
    },

    async setDocDataVariables() {
      this.is_view_readonly = readOnlyDocStatus.includes(this.record.status);
      this.extractable = this.isDocExtractable();
      this.has_ai_table = await this.isAITableVisible(this.record);
      this.setFormatLocale();
      this.setDocTypeAndLanguageOrigin();
      this.setTableVersion();
      await this.setPoMatchingData();
      this.doc_status = this.record.status ? this.record.status : '';
      this.document_version = this.record.document_version
        ? this.record.document_version
        : 0;
      const is_manual_training_allowed = this.has_ai_table ? false : true;
      this.setManualTraining(is_manual_training_allowed);

      this.handlePoItems();
      if (this.record.hasOwnProperty('tfidf_id') && this.record.tfidf_id) {
        this.supplier = this.record.tfidf_id;
      }

      this.remove_unmapped_columns = this.setUnMappedColumnsSetting();

      this.setScriptErrorCount();
      if (!this.record.images) {
        return;
      }

      for (let i = 1; i <= this.record.images.length; i++) {
        this.pages_list.push(Object.assign({}, { value: i, label: i }));
      }
    },

    setScriptErrorCount() {
      this.script_error_count = 0;
      if (!this.record.hasOwnProperty('script_execution')) {
        return;
      }
      for (const [index, item] of Object.entries(
        this.record.script_execution,
      )) {
        const record = item as dynamicObj;
        if (!record.success) {
          this.script_error_count += 1;
        }
      }
      // first check if settings is on
    },

    setUnMappedColumnsSetting() {
      if (
        !this.record.hasOwnProperty('tables') ||
        !Array.isArray(this.record.tables) ||
        this.record.tables.length == 0 ||
        !this.record.tables[0].hasOwnProperty('remove_unmapped_columns')
      ) {
        return true;
      }

      if (this.record.tables[0].remove_unmapped_columns === false) {
        return false;
      }

      return true;
    },

    setFormatLocale() {
      if (!this.record.amount_format_locale) {
        return;
      }

      const localeObj = this.record.amount_format_locale.split('.');
      this.amount_format_locale = localeObj[0];
    },

    setDocType() {
      this.sub_doc_type = this.record.sub_doc_type
        ? this.record.sub_doc_type
        : '';
      this.doctype = this.record.doc_type ? this.record.doc_type : '';

      if (this.doctype) {
        this.selected_doc_type = this.doctype;
      }

      if (!this.sub_doc_type) {
        return;
      }

      this.selected_doc_type = this.sub_doc_type;
    },

    setDocTypeAndLanguageOrigin() {
      this.language_origin = this.record.doc_origin
        ? this.record.doc_origin
        : '';
      this.setDocType();
    },

    async autoDetectTables(params: dynamicObj = {}) {
      try {
        const data = {
          doc_id: this.record.doc_id,
        } as dynamicObj;
        // merge params with data
        Object.assign(data, params);

        if (this.record.hasOwnProperty('is_table_training_doc')) {
          data['is_table_training_doc'] = true.toString();
        }

        const response = await documentDetailApiService.autoDetectTables(data);
        if (response.success === false) {
          throw { message: response.message };
        }

        this.setTablesData(response);
      } catch (err) {
        logError(err, '[DOCUMENT] Failed to auto-detect tables');
        throw err;
      }
    },

    setTablesData(response: dynamicObj) {
      this.record.tables = response;
    },

    isAI_TableExit(record: dynamicObj) {
      if (
        !record.hasOwnProperty('tables') ||
        !Array.isArray(record.tables) ||
        record.tables.length == 0 ||
        !record.tables[0].hasOwnProperty('is_ai_table') ||
        record.tables[0].is_ai_table === false
      ) {
        return false;
      }

      return true;
    },

    async isAITableVisible(record: dynamicObj) {
      if (!this.isAI_TableExit(record)) {
        return false;
      }

      const settings_store = useSettingStore();
      const ai_table_setting =
        await settings_store.user_settings[AI_table_extraction_setting];
      if (toBoolPreference(ai_table_setting)) {
        return true;
      }

      return false;
    },

    async setTableVersion() {
      let TBL_EXT_VERSION = '';
      if (this.record.doc_type != 'XML_INVOICE') {
        const settings_store = useSettingStore();
        TBL_EXT_VERSION = await settings_store.getSetting('TBL_EXT_VERSION');
      }
      if (
        TBL_EXT_VERSION !== undefined &&
        TBL_EXT_VERSION != '' &&
        TBL_EXT_VERSION == 'Version3'
      ) {
        this.tb_version = 'v3';
      }
    },

    async exportDocument(id: string): Promise<dynamicObj> {
      this.saving = true;
      const data: any = JSON.parse(JSON.stringify(this.record));
      const response: dynamicObj = await documentDetailApiService.export(
        data,
        id,
      );
      if (response.success !== false) {
        this.record = response.data;
        await this.setDocDataVariables();
        // Will be removed once api response is fixed
        if (!this.record.hasOwnProperty('layout')) {
          this.record.layout = data.layout;
        }
        this.saving = false;
      }

      return response;
    },

    async getDocAnnotations(doc_id: string) {
      try {
        const response = await documentDetailApiService.getAnnotationsFromDoc(doc_id);
        const payload = Array.isArray(response) ? response : response?.data;
        this.doc_annotations = Array.isArray(payload) ? payload : [];
      } catch (err) {
        console.error('[DOCUMENT] Failed to get document annotations', err);
        this.doc_annotations = [];
      }
    },

    async saveAndValidate() {
      try {
        this.old_fields_state = this.getFieldsOldState();
        const data: any = JSON.parse(JSON.stringify(this.record));

        //Setting param in data for quotes specific scenerio
        if (this.ignore_doc_type_specific_validation) {
          data['ignore_doc_type_specific_validation'] = true;
          this.ignore_doc_type_specific_validation = false;
        }
        this.cleanupDataforApi(data);
        const response: dynamicObj =
          await documentDetailApiService.saveAndValidate(
            data,
            this.record.doc_id,
          );

        if (
          response.success === false &&
          response.error_code != 'DOC_NEEDS_VALIDATION'
        ) {
          throw response;
        }

        if (response.hasOwnProperty('data')) {
          this.setDataToStoreAndStorage(response.data);
          this.saveFieldsOldState(this.record);
        }

        return response;
      } catch (err) {
        const msg = ApiExceptions.getErrorMessage(err as dynamicObj);
        logError(err);
        return {
          success: false,
          message: msg,
        };
      }
    },

    async setPoMatchingData() {
      this.po_enable =
        (await poActionsUtil.methods.isPoSettingActivate(this.doctype)) &&
        poActionsUtil.methods.isPoIconAllowed(
          this.record.status,
          this.tb_version,
        );
    },

    setManualTraining(value: boolean) {
      this.is_manual_training_allowed = value;
    },

    isElectronicDocument() {
      if (this.record.extraction_method === 'ELECTRONIC_DOCUMENT') {
        return true;
      }
      return false;
    },

    getDocSubOrgId() {
      let sub_org_id = '';
      if (!this.record) {
        return sub_org_id;
      }

      if (this.record.hasOwnProperty('sub_org_id')) {
        sub_org_id = this.record.sub_org_id;
      }
      return sub_org_id;
    },
    saveFieldsOldState(record: dynamicObj) {
      // logInfo('saveFieldsOldState', record.fields);
      SessionStorage.set(this.key_old_fields_state, record.fields);
    },

    getFieldsOldState() {
      // logInfo('getFieldsOldState');
      const fields = SessionStorage.getItem(this.key_old_fields_state);
      if (fields) {
        return fields as dynamicObj[];
      }
      return [] as dynamicObj[];
    },

    saveOneFieldStateForAutotrigger(record: dynamicObj, field_name: string) {
      const fields = this.getFieldsStateForAutotrigger();

      const field = record.fields.find(
        (item: dynamicObj) => item.name === field_name,
      );
      // delete field from state
      if (!field) {
        for (let i = 0; i < fields.length; i++) {
          if (fields[i].name === field_name) {
            fields.splice(i, 1);
            break;
          }
        }
      }

      if (field) {
        for (let i = 0; i < fields.length; i++) {
          if (fields[i].name === field_name) {
            fields[i] = field;
            break;
          }
        }
      }

      this.saveFieldsStateForAutotrigger(fields);
    },

    saveFieldsStateForAutotrigger(fields: dynamicObj[]) {
      SessionStorage.set(this.saved_fields_for_autotrigger, fields);
    },

    getFieldsStateForAutotrigger() {
      const fields = SessionStorage.getItem(this.saved_fields_for_autotrigger);
      if (fields) {
        return fields as dynamicObj[];
      }
      return [] as dynamicObj[];
    },

    haveStateFieldsChanged(record: dynamicObj) {
      const fields = this.getFieldsStateForAutotrigger();
      if (fields && fields != record.fields) {
        return true;
      }
      return false;
    },

    async rejectDocument(id: string) {
      try {
        const data = {
          request_type: 'reject',
        };
        return await this.approveOrRejectDocument(id, data);
      } catch (err) {
        logError(err, '[DOCUMENT] Failed to reject document');
        throw err;
      }
    },

    async approveOrRejectDocument(id: string, data: dynamicObj) {
      try {
        const doc_save_response = await this.saveDocument(id, true);
        if (doc_save_response.success === false) {
          throw { message: doc_save_response.message };
        }

        const response: dynamicObj =
          await documentDetailApiService.approve_or_reject_document(id, data);
        return response;
      } catch (err) {
        logError(err, '[DOCUMENT] Failed to approve/reject document');
        throw err;
      }
    },

    async isPoTableInLayoutBuilder() {
      const doc_attribute_store = useDocAttributesStore();
      const { model } = doc_attribute_store;
      if (!model.hasOwnProperty(po_table_in_layout_builder)) {
        model[po_table_in_layout_builder] = false;
      }

      if (!this.doc_loaded) {
        return model[po_table_in_layout_builder] as boolean;
      }

      await doc_attribute_store.fetchAttributes(this.selected_doc_type);
      return model[po_table_in_layout_builder] as boolean;
    },

    async handlePoItems() {
      try {
        if (!this.fetch_po_items) {
          return;
        }
        const is_po_table_in_layout_builder =
          await this.isPoTableInLayoutBuilder();
        if (!is_po_table_in_layout_builder || this.po_items_loading) {
          return;
        }
        this.po_items_loaded = false;
        this.po_items_loading = true;
        let po_items = {} as dynamicObj;
        po_items = this.getPurchaseOrderData();
        for (const [key, value] of Object.entries(po_items)) {
          this.addToPoItems(key, value);
        }

        const po_numbers = this.getDocPoNumbers();
        // logInfo('handlePoItems::po_numbers', po_numbers);
        // logInfo('handlePoItems', po_items);
        if (readOnlyDocStatus.includes(this.record.status)) {
          return;
        }

        for (const po_number of po_numbers) {
          // logInfo('handlePoItems::addItems', po_numbers);
          await this.fetchAddPoItems(po_number);
        }
      } catch (e) {
        logError(e);
      } finally {
        this.po_items_loading = false;
        this.po_items_loaded = true;
      }
    },

    getDocPoNumbers() {
      const po_numbers = [] as string[];
      const header_po_number = this.addPoItemsFromHeaderPO();
      if (!isEmptyString(header_po_number)) {
        this.header_po_number = header_po_number;
        po_numbers.push(header_po_number);
      }
      const table_po_numbers = this.getTablePoNumbers();
      po_numbers.push(...table_po_numbers);
      return po_numbers;
    },

    addPoItemsFromHeaderPO() {
      if (
        isUndefinedOrNull(this.record.fields) ||
        this.record.fields.length == 0
      ) {
        return '';
      }

      const field = this.record.fields.find(
        (item: dynamicObj) => item.name === 'purchase_order',
      );
      if (!field) {
        return '';
      }

      if (
        !field.hasOwnProperty('formatted_value') ||
        isUndefinedOrNull(field.formatted_value)
      ) {
        return '';
      }

      return String(field.formatted_value);
      // await this.fetchAddPoItems(field.formatted_value);
    },

    getTablePoNumbers() {
      const table_po_numbers = [] as string[];
      if (
        !this.record.tables ||
        this.record.tables.length == 0 ||
        !this.record.tables[0].hasOwnProperty('rows') ||
        this.record.tables[0].rows.length == 0
      ) {
        return table_po_numbers;
      }

      for (const [index, row] of this.record.tables[0].rows.entries()) {
        if (row.is_deleted === true) {
          continue;
        }

        for (const [index, column] of row.columns.entries()) {
          if (
            !this.purchase_order_columns.includes(
              column.name ? column.name : column.name?.toLowerCase(),
            ) ||
            column.value === ''
          ) {
            continue;
          }

          table_po_numbers.push(column.value);
        }
      }

      return table_po_numbers;
    },

    async fetchAddPoItems(purchase_order: string) {
      if (this.po_items[purchase_order]) {
        return;
      }

      const po_items = (await this.getPOitems(purchase_order)) as dynamicObj;
      this.addToPoItems(purchase_order, po_items, true);
    },

    addToPoItems(purchaseOrder: string, po_items: dynamicObj, save = false) {
      if (isUndefinedOrNull(this.po_items)) {
        this.po_items = {} as dynamicObj;
      }

      // Skip empty data only when NOT a save/refresh operation
      if (objLen(po_items) == 0 && !save) {
        return;
      }

      // Skip if already exists and not a save/refresh operation
      if (this.po_items.hasOwnProperty(purchaseOrder) && !save) {
        return;
      }

      this.po_items[purchaseOrder] = po_items;

      if (save) {
        this.setPoItemsforSave();
        return;
      }
    },

    setPoItemsforSave() {
      if (!this.record) {
        return;
      }

      if (!this.record.hasOwnProperty('po_items')) {
        this.record.po_items = {} as dynamicObj;
      }

      this.record.po_items = this.po_items;
    },

    async refreshPOItems(purchase_order: string) {
      try {
        if (this.is_view_readonly) {
          return;
        }
        const po_items = (await this.getPOitems(purchase_order)) as dynamicObj;
        this.addToPoItems(purchase_order, po_items, true);
      } catch (err) {
        logError(err);
      }
    },

    async getPOitems(purchase_order: string) {
      let purchase_order_items = [] as dynamicObj[];
      try {
        if (readOnlyDocStatus.includes(this.record.status) || this.record.status == 'error' || isEmptyString(purchase_order)) {
          return;
        }

        const data = {} as dynamicObj;
        data.limit = -1;
        data.doc_type = this.record.doc_type;
        data.purchase_order_number = purchase_order;
        const response = await documentPOApiService.getPOitems(data);
        if (response.success === false) {
          throw response;
        }

        if (response.success !== false) {
          purchase_order_items = response;
        }
      } catch (err) {
        logError(err);
      }

      return purchase_order_items;
    },

    getPurchaseOrderData() {
      let po_items = {} as dynamicObj;
      if (!this.record || !this.record.po_items) {
        return po_items;
      }

      po_items = this.record.po_items;
      return po_items;
    },

    proceedToClosePOTab(index: string) {
      if (this.is_view_readonly) {
        return;
      }

      if (!index) {
        this.removeFromPoItems(index);
        return;
      }

      if (this.isPODeleteAble(index)) {
        this.delete_tab_po_number = index;
        this.delete_tab_confirmation = true;
      }
    },

    removeFromPoItems(purchaseOrder: string, save = true) {
      if (isUndefinedOrNull(this.po_items) || objLen(this.po_items) == 0) {
        return;
      }
      delete this.po_items[purchaseOrder];
      if (save) {
        this.setPoItemsforSave();
      }
    },

    isPODeleteAble(po_number: string) {
      if (isUndefinedOrNull(po_number) || isEmptyString(po_number)) {
        return true;
      }

      if (po_number === this.header_po_number) {
        return false;
      }
      return true;
    },

    deletePoTab() {
      if (!this.delete_tab_po_number || this.delete_tab_po_number === '') {
        return;
      }
      this.removeFromPoItems(this.delete_tab_po_number);
      this.removePoConnection(this.delete_tab_po_number);
    },

    removePoConnection(po_number: string) {
      if (!this.record || !this.record.po_multi_matched) {
        return;
      }
      const found_indexes = [] as dynamicObj[];
      for (const [index, record] of this.record.po_multi_matched.entries()) {
        const purchase_order_numbers: string[] =
          this.getPurchaseOrderNumbers(record);

        for (const purchase_order_number of purchase_order_numbers) {
          if (po_number == purchase_order_number) {
            found_indexes.push(index);
          }
        }
      }

      for (let i = found_indexes.length - 1; i >= 0; i--)
        this.record.po_multi_matched.splice(found_indexes[i], 1);

      return found_indexes;
    },
    getPurchaseOrderNumbers(record: any) {
      const purchase_order_numbers: string[] = [];

      if (record.hasOwnProperty('po_line_id')) {
        purchase_order_numbers.push(record.purchase_order_number);
      } else if (record.hasOwnProperty('sub_lines')) {
        for (const [index, po_sub_lines] of record.sub_lines.entries()) {
          purchase_order_numbers.push(po_sub_lines.purchase_order_number);
        }
      }
      return purchase_order_numbers;
    },

    hasTable() {
      try {
        if (
          !this.record.hasOwnProperty('tables') ||
          this.record.tables.length == 0 ||
          !this.record.tables[0].hasOwnProperty('rows')
        ) {
          return false;
        }
        return true;
      } catch (error) {
        return false;
      }
    },
    async prepareDocumentTableList(docType: string) {
      this.tables_list = [];
      this.tables_list.push({ value: '', label: i18n.t('select_table') });

      try {
        const response = await documentTypeApiService.getTable(docType);

        if (response.data && !response.success) {
          throw { message: response.data.message };
        }

        response.data.forEach((element: any) => {
          this.tables_list.push({
            value: element.table_name,
            label: element.table_name,
          });
        });
      } catch (err) {
        const msg = ApiExceptions.getErrorMessage(err as dynamicObj);
        logError(err, msg);
      }
    },

    checkForApprovalpermission(field: string) {

      if (this.authTokenStore.isAdmin()) {
        return true;
      }

      if (
        this.tablePermissions[field] &&
        this.tablePermissions[field].access == 16
      ) {
        return true;
      }

      if (
        this.fieldPermissions[field] &&
        this.fieldPermissions[field].access == 16
      ) {
        return true;
      }

      return false;
    },
    getChargesNameByCostingElement(costingElement: string) {
      return this.chargesFields[costingElement]
        ? String(this.chargesFields[costingElement].field_name).toLowerCase()
        : costingElement;
    },
    async prepareChargesFields() {
      await this.docTypeFieldsStore.getFieldSettingsByType(
        this.record.doc_type,
      );
      const groups =
        this.docTypeFieldsStore.docTypesFieldsGroup[this.record.doc_type];
      if (!groups || groups.length == 0) {
        return;
      }

      groups.forEach((group: any, groupIndex: any) => {
        group.rows.forEach((subGroup: any, subIndex: any) => {
          subGroup.forEach((field: any, index: any) => {
            if (field.costing_element) {
              this.chargesFields[field.costing_element] = field;
            }
          });
        });
      });
    },

    prepareFormula(calculationFormula: string) {
      let newFormula = calculationFormula;

      let result2 = null;
      const pattern = /\[(.*?)\]/g;

      while ((result2 = pattern.exec(calculationFormula)) !== null) {
        const field = result2[1];

        const subGroupChildFields = this.findSubGroupChildFields(field);

        if (
          subGroupChildFields &&
          Object.keys(subGroupChildFields).length == 0
        ) {
          continue;
        }

        const formulaArray = [];
        formulaArray.push('[' + field + ']');

        for (const item of subGroupChildFields) {
          formulaArray.push('[' + item.name + ']');
        }

        const generatedFormula = formulaArray.join(' + ');
        // generatedFormula = '(' + generatedFormula + ')';

        const search = '[' + field + ']';

        newFormula = newFormula.replace(search, generatedFormula);
      }

      return newFormula;
    },

    findSubGroupChildFields(fieldName: string) {
       
      return (
        this.record.fields
           
          .filter(
            (item: any) =>
              item.name.startsWith(fieldName + '__') && item.name !== fieldName,
          )
           
          .sort((a: any, b: any) =>
            a.name.localeCompare(b.name, undefined, { numeric: true }),
          )
      );
    },

    // accounting effects by matched po
    removeAccountingData() {
      if (this.record.hasOwnProperty('accounting')) {
        delete this.record.accounting;
      }
    },

    shouldConfirmAccountingReset(fieldName: string) {
      if (!this.record.accounting) {
        return false;
      }
      if (restAccountingByFields.includes(fieldName)) {
        return true;
      }
      const { user_settings } = useSettingStore();
      if (!user_settings?.[auto_accounting_reset_data_on_change_setting]) {
        return false;
      }
      return ACCOUNTING_ELEMENT_FIELD_PREFIXES.some((prefix) =>
        fieldName.startsWith(prefix),
      );
    },

    isPurchaseInvoice() {
      for (const field of this.record.fields) {
        if (field.name !== CONSTANTS.invoice_sub_type) {
          continue;
        }
        return field.formatted_value == CONSTANTS.purchase_invoice
          ? true
          : false;
      }
    },
    isTypeQuote() {
      return this.record.doc_type === QUOTE;
    },
  },
});
